import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import GroupSelection from "../styleSettings/GroupSelection";
import SubgroupSelection from "../styleSettings/SubgroupSelection";
import TopBar from "../styleSettings/TopBar";
import Scrollbar from "../../scroll/Scrollbar";
import SongName from "./SongName";
import PlaySongProgressBar from "../../progressbar/PlaySongProgressBar";
import * as Tone from "tone";
import {
    FaPlay,
    FaPause,
    FaStepBackward,
    FaRedoAlt,
} from "react-icons/fa";
import Button from "../../buttons/Button";
import CircularProgressBar from "../../progressbar/CircularProgressBar";
import {
    loadSynth,
    disposeSynths,
    createBufferedMidiEvents,
    getTuning,
} from "../../../util/PlayerUtil.js";
import { delay } from "../../../util/AppUtil.js";
import {
    MELOGY_ALMOST_OPAQUE,
    MELOGY_TRANSPARENT,
    MELOGY_WHITE,
    MELOGY_BLACK,
} from "../../../style/colors";
import {
    FONT_SIZE_2XS,
    TITLE_FONT_FAMILY,
    TITLE_FONT_WEIGHT,
    TITLE_LETTER_SPACING,
} from "../../../style/fontSettings";
import { POPOVER_SHADOW } from "../../../style/shadows";
import StyleSetting from "../styleSettings/StyleSetting";
import { 
    findFarthestColor, 
    getStyleColor,
    getStyleBackgroundImage,
} from '../../../util/StyleSettingsUtil';

const Overlay = styled.div`
    position: fixed;
    top: 110px;
    left: 0;
    width: 100%;
    height: calc(100vh - 220px);
    background-color: ${MELOGY_TRANSPARENT};
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
`;

const OverlayContentBackground = styled.div`
    width: 100%;
    max-width: 960px;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const OverlayContent = styled.div`
    background-color: ${({ backgroundColor }) => (backgroundColor ? backgroundColor : "#000")};
    background-image: ${({ backgroundImage }) => backgroundImage ? `url(${backgroundImage})` : 'none'};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    width: 80%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;
    position: relative;
    border-radius: 16px;
    backdrop-filter: blur(50px);
    -webkit-backdrop-filter: blur(50px);
    padding: 8px;
    box-sizing: border-box;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${({ backgroundColor }) => (backgroundColor ? backgroundColor : "#000")};
        opacity: ${({ backgroundImage }) => backgroundImage ? 0 : 1};
        border-radius: 16px;
        z-index: 0;
        display: ${({ backgroundImage }) => backgroundImage ? 'none' : 'block'};
    }
    
    & > * {
        position: relative;
        z-index: 1;
    }
`;

const OverlayContentMain = styled.div`
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    overflow-y: auto;
    overflow-x: hidden;
`;

const UnifiedContainer = styled.div`
    width: 80%;
    display: flex;
    flex-direction: column;
    background-color: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    border-radius: 4px;
    padding: 8px;
    gap: 0;
`;

const StyleSettingContainerOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100vh - 212px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 500;
    background-color: ${MELOGY_TRANSPARENT};
`;

const StyleSettingContainer = styled.div`
    width: 100%;
    max-width: 960px;
    background-color: ${MELOGY_TRANSPARENT};
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    position: absolute; 
    z-index: 501;
    padding: 8px;
`;

const StyleSettingBackground = styled.div`
    width: ${({ isColorPicker }) => (isColorPicker ? "225px" : "calc(80% - 64px)")};
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: ${MELOGY_ALMOST_OPAQUE};
    box-shadow: ${POPOVER_SHADOW};
`;

const GroupTitle = styled.div`
    width: 100%;
    height: 32px;
    font-size: ${FONT_SIZE_2XS};
    font-family: ${TITLE_FONT_FAMILY};
    font-weight: ${TITLE_FONT_WEIGHT};
    letter-spacing: ${TITLE_LETTER_SPACING};
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${MELOGY_WHITE};
    user-select: none;
`;

const SongPlayerContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-height: 80px;
    justify-content: center;
    padding: 8px;
`;

const ProgressContainer = styled.div`
    height: 16px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
`;

const SongPlayerControls = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    position: relative;
`;

const SimpleControlsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 8px;
`;

// Individual song player component
const SongPlayer = ({ 
    song, 
    isActive, 
    isPlaying,
    progress,
    selectedInstruments,
    selectedStyle,
    onPlayPause,
    onProgressChange,
    onRewind,
    onLoop,
    isLooping,
    isInitializing,
}) => {
    const midi = song ? song.midi : null;
    
    if (!midi) {
        return (
            <SongPlayerContainer>
                <div style={{ color: MELOGY_WHITE }}>No MIDI data</div>
            </SongPlayerContainer>
        );
    }

    return (
        <SongPlayerContainer onClick={(e) => e.stopPropagation()}>
            {isActive && (
                <>
                    <ProgressContainer>
                        <PlaySongProgressBar
                            progress={progress}
                            handleDrag={onProgressChange}
                            handleClick={onProgressChange}
                        />
                    </ProgressContainer>
                    <SongPlayerControls>
                        <SimpleControlsContainer>
                            <Button
                                height="32px"
                                width="32px"
                                justifyContent="flex-start"
                                buttonColor="white"
                                onClick={onRewind}
                                icon={<FaStepBackward style={{ fontSize: "16px" }} />}
                            />
                            {isInitializing ? (
                                <CircularProgressBar />
                            ) : (
                                <Button
                                    height="32px"
                                    width="32px"
                                    justifyContent="flex-start"
                                    buttonColor="white"
                                    onClick={onPlayPause}
                                    icon={
                                        isPlaying ? (
                                            <FaPause style={{ fontSize: "16px" }} />
                                        ) : (
                                            <FaPlay style={{ fontSize: "16px" }} />
                                        )
                                    }
                                />
                            )}
                            <Button
                                height="32px"
                                width="32px"
                                justifyContent="flex-start"
                                buttonColor="white"
                                onClick={onLoop}
                                icon={<FaRedoAlt style={{ fontSize: "16px" }} />}
                                isSelected={isLooping}
                            />
                        </SimpleControlsContainer>
                    </SongPlayerControls>
                </>
            )}
        </SongPlayerContainer>
    );
};

const SongSettings = ({ selectedSong, closeOverlay, setSelectedSong, setSongSettingsIsOpen, updateSong, selectedInstruments, selectedStyle, setSongName, isNameUnique }) => {
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [subgroupsOfSelectedGroup, setSubgroupsOfSelectedGroups] = useState([]);
    const [selectedSubgroup, setSelectedSubgroup] = useState(null);
    const [selectedSettings, setSelectedSettings] = useState([]);
    
    // Only allow Background and Instruments groups
    const allowedGroups = ["Background", "Instruments"];
    const songStyle = selectedSong?.style || [];
    
    const [displayValues, setDisplayValues] = useState(
        songStyle.reduce((acc, setting) => {
            acc[setting.name] = setting.value;
            return acc;
        }, {})
    );

    // Update displayValues when songStyle changes
    useEffect(() => {
        const newDisplayValues = songStyle.reduce((acc, setting) => {
            acc[setting.name] = setting.value;
            return acc;
        }, {});
        setDisplayValues(newDisplayValues);
    }, [songStyle]);
    const [subgroupSelectionPopoverIsOpen, setSubgroupSelectionPopoverIsOpen] = useState(false);
    const [settingsPopoverIsOpen, setSettingsPopoverIsOpen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [totalDuration, setTotalDuration] = useState(0);
    const [activeSynths, setActiveSynths] = useState([]);
    const [isToneInitialized, setIsToneInitialized] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [isLooping, setIsLooping] = useState(false);
    const styleSettingRef = useRef(null);
    const overlayRef = useRef(null);
    const overlayContentRef = useRef(null);
    const activeParts = useRef([]);
    const lastPlayedSongRef = useRef(null);
    const prevSelectedSongRef = useRef(null);
    const [backgroundColor, setBackgroundColor] = useState(getStyleColor(songStyle));
    const [backgroundImage, setBackgroundImage] = useState(getStyleBackgroundImage(songStyle));
    
    useEffect(() => {
        setBackgroundColor(getStyleColor(songStyle));
        setBackgroundImage(getStyleBackgroundImage(songStyle));
    }, [songStyle]);
    
    const buttonColor = findFarthestColor(backgroundColor);
    // Always use white text/icons for better readability with dark backgrounds
    const color = MELOGY_WHITE;
    const tuning = getTuning(songStyle);
    
    // Extract instruments from the song's style settings
    const songInstruments = React.useMemo(() => {
        return songStyle ? songStyle.filter((item) => item.group === "Instruments") : [];
    }, [songStyle]);

    const updateSelectedSettings = useCallback(() => {
        const selectedSettings = songStyle.filter(setting => 
            setting.group === selectedGroup && 
            (!setting.subgroup || setting.subgroup === selectedSubgroup) &&
            allowedGroups.includes(setting.group)
        );
        setSelectedSettings(selectedSettings);
    }, [songStyle, selectedGroup, selectedSubgroup]);

    useEffect(() => {
        updateSelectedSettings();
    }, [updateSelectedSettings]);

    const initializeTone = async () => {
        if (!isToneInitialized && songInstruments && songInstruments.length > 0) {
            const newSynths = [];
            for (let i = 0; i < songInstruments.length; i++) {
                newSynths[i] = await loadSynth(songInstruments[i]);
            }
            await Tone.loaded();
            disposeSynths(activeSynths);
            setActiveSynths(newSynths);
            setIsToneInitialized(true);
            return newSynths;
        }
        return activeSynths;
    };

    const loadAndPlayMidiForSong = useCallback(async (song, synthsToUse = null) => {
        if (!song || !song.midi) return;
        
        const synths = synthsToUse || activeSynths;
        
        try {
            Tone.Transport.cancel(0);
            activeParts.current.forEach((part) => part.stop(0));
            activeParts.current.length = 0;

            await Tone.start().catch((error) => {
                console.log("Couldn't start AudioContext:", error);
            });

            if (synths.length === 0) {
                console.log("No synths loaded.");
                return;
            }

            const { parts, maxDuration } = await createBufferedMidiEvents(
                song.midi.tracks,
                synths,
                songInstruments,
                tuning
            );

            parts.forEach((part) => {
                activeParts.current.push(part);
                part.start(0);
            });

            setTotalDuration(maxDuration);
            if (Tone.Transport.state === "started") {
                Tone.Transport.stop();
            }
            Tone.Transport.start("+0.1");

            Tone.Transport.scheduleOnce(() => {
                if (isLooping) {
                    Tone.Transport.position = 0;
                    loadAndPlayMidiForSong(song);
                } else {
                    Tone.Transport.stop();
                    Tone.Transport.position = 0;
                    setProgress(0);
                    setIsPlaying(false);
                }
            }, maxDuration);
        } catch (err) {
            console.error("An error occurred in loadAndPlayMidiForSong: ", err);
        }
    }, [songInstruments, activeSynths, tuning, isLooping]);

    const loadAndPlayMidi = useCallback(async () => {
        if (!selectedSong || !selectedSong.midi) return;
        await loadAndPlayMidiForSong(selectedSong);
    }, [selectedSong, loadAndPlayMidiForSong]);

    useEffect(() => {
        if (isPlaying && selectedSong && activeSynths.length > 0) {
            const songKey = selectedSong.name;
            if (lastPlayedSongRef.current !== songKey || Tone.Transport.state !== "started") {
                lastPlayedSongRef.current = songKey;
                loadAndPlayMidiForSong(selectedSong);
            }
        }
    }, [activeSynths, isPlaying, selectedSong, loadAndPlayMidiForSong]);

    useEffect(() => {
        if (isPlaying && selectedSong) {
            const interval = setInterval(() => {
                const newProgress = (Tone.Transport.seconds / totalDuration) * 100;
                setProgress(newProgress);
            }, 100);
            return () => clearInterval(interval);
        }
    }, [isPlaying, totalDuration, selectedSong]);

    useEffect(() => {
        if (selectedSong) {
            if (prevSelectedSongRef.current !== selectedSong) {
                Tone.Transport.stop();
                Tone.Transport.position = 0;
                setProgress(0);
                setIsPlaying(false);
                prevSelectedSongRef.current = selectedSong;
            }
        }
    }, [selectedSong]);

    useEffect(() => {
        const updateSynths = async () => {
            if (!isToneInitialized || !songInstruments || songInstruments.length === 0) {
                return;
            }
            const newSynths = [];
            for (let i = 0; i < songInstruments.length; i++) {
                newSynths[i] = await loadSynth(songInstruments[i]);
            }
            disposeSynths(activeSynths);
            setActiveSynths(newSynths);
            
            // If currently playing, reload the MIDI with new instruments
            if (isPlaying && selectedSong) {
                // Use the new synths directly instead of waiting for state update
                loadAndPlayMidiForSong(selectedSong, newSynths);
            }
        };
    
        const debounceTimeout = setTimeout(() => {
            updateSynths();
        }, 300); 
    
        return () => clearTimeout(debounceTimeout);
    }, [songInstruments, isToneInitialized, isPlaying, selectedSong, loadAndPlayMidiForSong]); 

    const handlePlayPause = async () => {
        if (!selectedSong || !selectedSong.midi) return;

        if (!isToneInitialized) {
            setIsInitializing(true);
            await Tone.loaded();
            await Tone.start();
            await initializeTone();
            await delay(2000);
            await delay(500);
            setIsInitializing(false);
        }

        if (isPlaying) {
            Tone.Transport.pause();
            setIsPlaying(false);
        } else {
            setIsPlaying(true);
        }
    };

    const handleProgressChange = (e) => {
        if (!selectedSong) return;
        const progressBar = e.target.closest(".progress-bar");
        if (!progressBar) return;
        const { left, width } = progressBar.getBoundingClientRect();
        const x = e.clientX - left;
        const newProgress = Math.min(Math.max((x / width) * 100, 0), 100);
        const newTime = (newProgress / 100) * totalDuration;
        setProgress(newProgress);
        Tone.Transport.seconds = newTime;
        if (isPlaying) {
            Tone.Transport.start("+0.1", newTime);
        }
    };

    const handleRewind = () => {
        if (!selectedSong) return;
        Tone.Transport.stop();
        Tone.Transport.position = 0;
        setProgress(0);
        if (isPlaying && selectedSong.midi) {
            loadAndPlayMidi();
        }
    };

    const handleLoop = () => {
        setIsLooping(!isLooping);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedInsideOverlay = overlayRef.current?.contains(event.target);
            const clickedOutsideOverlayContent = !overlayContentRef.current?.contains(event.target);
            const clickedOutsideStyleSetting = !styleSettingRef.current?.contains(event.target);
    
            const shouldUnselectSong = 
                clickedInsideOverlay &&
                clickedOutsideOverlayContent &&
                !settingsPopoverIsOpen &&
                !subgroupSelectionPopoverIsOpen;
    
            const shouldCloseSettingsPopover = 
                clickedInsideOverlay &&
                clickedOutsideStyleSetting &&
                settingsPopoverIsOpen;
    
            if (shouldUnselectSong) {
                handleUnselectSong();
            }
    
            if (shouldCloseSettingsPopover) {
                setSettingsPopoverIsOpen(false);
                setSubgroupSelectionPopoverIsOpen(subgroupsOfSelectedGroup.length > 0);
            }
        };
    
        document.addEventListener("mousedown", handleClickOutside);
        
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [settingsPopoverIsOpen, subgroupSelectionPopoverIsOpen, subgroupsOfSelectedGroup]);    

    const handleUnselectSong = () => {
        // Stop playback when closing
        Tone.Transport.stop();
        Tone.Transport.position = 0;
        setProgress(0);
        setIsPlaying(false);
        setSongSettingsIsOpen(false);
        closeOverlay();
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            Tone.Transport.stop();
            Tone.Transport.position = 0;
            disposeSynths(activeSynths);
            activeParts.current.forEach((part) => part.stop(0));
            activeParts.current.length = 0;
        };
    }, []);

    const handleSelectGroup = (groupName) => {
        // Only allow Color and Instruments groups
        if (!allowedGroups.includes(groupName)) {
            return;
        }
        
        const subgroupsOfSelectedGroup = getSubgroupsByGroup(groupName);

        if (subgroupsOfSelectedGroup.length > 0) {
            setSubgroupsOfSelectedGroups(subgroupsOfSelectedGroup);
            setSubgroupSelectionPopoverIsOpen(true);
        } else {
            setSubgroupsOfSelectedGroups([]);
            setSubgroupSelectionPopoverIsOpen(false);
            setTimeout(() => {
                setSettingsPopoverIsOpen(true);
            }, 25);
        }
        setSelectedGroup(groupName);
    };

    const getSubgroupsByGroup = (groupName) => {
        const subgroups = new Set();
        songStyle.forEach(item => {
            if (item.group === groupName && item.subgroup && allowedGroups.includes(item.group)) {
                subgroups.add(item.subgroup);
            }
        });
        return Array.from(subgroups);
    };

    const handleSelectSubgroup = (subgroup) => {
        setSelectedSubgroup(subgroup);
        setSubgroupSelectionPopoverIsOpen(false);
        setTimeout(() => {
            setSettingsPopoverIsOpen(true);
        }, 25);
    };

    const updateSongSettings = (settingName, newValue) => {
        updateSong(settingName, newValue);
        
        // Update display values
        setDisplayValues((prev) => ({
            ...prev,
            [settingName]: newValue,
        }));
    };

    const setSliderDisplayValue = (settingName, newDisplayValue) => {
        setDisplayValues((prev) => ({
            ...prev,
            [settingName]: newDisplayValue,
        }));
    };

    // Filter groups to only show Color and Instruments
    const availableGroups = Array.from(new Set(
        songStyle
            .filter(setting => allowedGroups.includes(setting.group))
            .map(setting => setting.group)
    ));

    const content = (
        <>
            {selectedSettings.map((styleSetting) => (
                <StyleSetting
                    key={styleSetting.name}
                    styleSetting={styleSetting}
                    updateStyleSettings={updateSongSettings}
                    sliderDisplayValue={displayValues[styleSetting.name]}
                    setSliderDisplayValue={(value) => setSliderDisplayValue(styleSetting.name, value)}
                />
            ))}
        </>
    );

    return (
        <Overlay ref={overlayRef}>
            <OverlayContentBackground>
                <OverlayContent 
                    ref={overlayContentRef}
                    backgroundColor={backgroundColor}
                    backgroundImage={backgroundImage}
                >
                    <TopBar
                        buttonColor="white"
                        onClose={handleUnselectSong}
                    />
                    <OverlayContentMain>
                        <UnifiedContainer>
                            <SongName 
                                selectedSong={selectedSong}
                                color={color}
                                setSongName={setSongName}
                                isNameUnique={isNameUnique}
                            />
                            <GroupSelection 
                                selectedStyle={songStyle}
                                handleSelectGroup={handleSelectGroup}
                                allowedGroups={allowedGroups}
                            />
                        </UnifiedContainer>
                    </OverlayContentMain>
                    <SongPlayer
                        song={selectedSong}
                        isActive={true}
                        isPlaying={isPlaying}
                        progress={progress}
                        selectedInstruments={songInstruments}
                        selectedStyle={songStyle}
                        onPlayPause={(e) => {
                            e?.stopPropagation();
                            handlePlayPause();
                        }}
                        onProgressChange={handleProgressChange}
                        onRewind={(e) => {
                            e?.stopPropagation();
                            handleRewind();
                        }}
                        onLoop={(e) => {
                            e?.stopPropagation();
                            handleLoop();
                        }}
                        isLooping={isLooping}
                        isInitializing={isInitializing}
                    />
                    {settingsPopoverIsOpen && (
                        <StyleSettingContainerOverlay>
                            <StyleSettingContainer>
                                <StyleSettingBackground 
                                    ref={styleSettingRef}
                                    isColorPicker={selectedSubgroup === 'Color'} 
                                >
                                    <GroupTitle>{selectedSubgroup ? selectedSubgroup : selectedGroup}</GroupTitle>
                                    {selectedSettings.length > 4 ? (
                                        <Scrollbar>{content}</Scrollbar>
                                    ) : (
                                        content
                                    )}
                                </StyleSettingBackground>
                            </StyleSettingContainer>
                        </StyleSettingContainerOverlay>
                    )}
                    {subgroupSelectionPopoverIsOpen && (
                        <SubgroupSelection 
                            selectedGroup={selectedGroup}
                            subgroups={subgroupsOfSelectedGroup}
                            onSelectSubgroup={handleSelectSubgroup}
                            onClose={() => {
                                setSubgroupSelectionPopoverIsOpen(false);
                                setSelectedSubgroup(null);
                            }}
                        />
                    )}
                </OverlayContent>
            </OverlayContentBackground>
        </Overlay>
    );
};

export default SongSettings;

